import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import './TicTacToeMinigame.css';

interface GameState {
  cells: string[];
  gameOver: boolean;
  winner: string | null;
  score: {
    marie: number;
    moulin: number;
  };
  difficultyLevel: number;
  showReset: boolean;
}

const TicTacToeMinigame: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<GameState>({
    cells: Array(9).fill(''),
    gameOver: false,
    winner: null,
    score: { marie: 0, moulin: 0 },
    difficultyLevel: 1,
    showReset: false
  });

  const winningCombinations = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8], // rows
    [0, 3, 6], [1, 4, 7], [2, 5, 8], // columns
    [0, 4, 8], [2, 4, 6] // diagonals
  ];

  const checkWinner = (board: string[]): string | null => {
    for (const combo of winningCombinations) {
      if (
        board[combo[0]] &&
        board[combo[0]] === board[combo[1]] &&
        board[combo[0]] === board[combo[2]]
      ) {
        return board[combo[0]];
      }
    }
    return null;
  };

  const checkDraw = (board: string[]): boolean => {
    return board.every(cell => cell !== '') && !checkWinner(board);
  };

  const minimax = (board: string[], depth: number, isMaximizing: boolean): number => {
    const winner = checkWinner(board);
    if (winner === 'O') return 10 - depth;
    if (winner === 'X') return depth - 10;
    if (checkDraw(board)) return 0;

    if (isMaximizing) {
      let bestScore = -Infinity;
      for (let i = 0; i < board.length; i++) {
        if (board[i] === '') {
          board[i] = 'O';
          const score = minimax(board, depth + 1, false);
          board[i] = '';
          bestScore = Math.max(score, bestScore);
        }
      }
      return bestScore;
    } else {
      let bestScore = Infinity;
      for (let i = 0; i < board.length; i++) {
        if (board[i] === '') {
          board[i] = 'X';
          const score = minimax(board, depth + 1, true);
          board[i] = '';
          bestScore = Math.min(score, bestScore);
        }
      }
      return bestScore;
    }
  };

  const getWorstMove = (board: string[]): number => {
    let worstScore = Infinity;
    let worstMove = -1;

    for (let i = 0; i < board.length; i++) {
      if (board[i] === '') {
        board[i] = 'O';
        const score = minimax(board, 0, false);
        board[i] = '';
        if (score < worstScore) {
          worstScore = score;
          worstMove = i;
        }
      }
    }
    return worstMove;
  };

  const getRandomMove = (board: string[]): number => {
    const emptyCells = board
      .map((cell, index) => cell === '' ? index : -1)
      .filter(index => index !== -1);
    return emptyCells[Math.floor(Math.random() * emptyCells.length)];
  };

  const getComputerMove = (board: string[]): number => {
    switch (state.difficultyLevel) {
      case 1:
        return getWorstMove(board);
      case 2:
        return getRandomMove(board);
      case 3:
        let bestScore = -Infinity;
        let bestMove = -1;
        
        for (let i = 0; i < board.length; i++) {
          if (board[i] === '') {
            board[i] = 'O';
            const score = minimax(board, 0, false);
            board[i] = '';
            if (score > bestScore) {
              bestScore = score;
              bestMove = i;
            }
          }
        }
        return bestMove;
      default:
        return getRandomMove(board);
    }
  };

  const handleGameEnd = (playerWins: boolean | 'draw') => {
    setState(prev => ({
      ...prev,
      gameOver: true,
      winner: playerWins === 'draw' ? 'draw' : playerWins ? 'player' : 'computer',
      score: playerWins === true 
        ? { ...prev.score, marie: prev.score.marie + 1 }
        : playerWins === false 
        ? { ...prev.score, moulin: prev.score.moulin + 1 }
        : prev.score,
      difficultyLevel: playerWins === true && prev.difficultyLevel < 3 
        ? prev.difficultyLevel + 1 
        : playerWins === false && prev.difficultyLevel > 1
        ? prev.difficultyLevel - 1
        : prev.difficultyLevel
    }));

    setTimeout(() => {
      setState(prev => ({ ...prev, showReset: true }));
    }, 1000);
  };

  const handleCellClick = (index: number) => {
    if (state.cells[index] === '' && !state.gameOver) {
      const newCells = [...state.cells];
      newCells[index] = 'X';

      if (checkWinner(newCells)) {
        handleGameEnd(true);
        return;
      }

      if (checkDraw(newCells)) {
        handleGameEnd('draw');
        return;
      }

      // Computer's move
      const computerMove = getComputerMove(newCells);
      if (computerMove !== -1) {
        newCells[computerMove] = 'O';
        setState(prev => ({ ...prev, cells: newCells }));

        if (checkWinner(newCells)) {
          handleGameEnd(false);
          return;
        }

        if (checkDraw(newCells)) {
          handleGameEnd('draw');
          return;
        }
      }
    }
  };

  const handleReset = () => {
    setState(prev => ({
      ...prev,
      cells: Array(9).fill(''),
      gameOver: false,
      winner: null,
      showReset: false
    }));
  };

  return (
    <div className="tictactoe-container">
      <button className="close-button" onClick={() => history.push('/')}>X</button>
      {state.gameOver && (
        <>
          <div className="winner-message">
            {state.winner === 'player' ? "Marie wins!" : 
             state.winner === 'computer' ? "The Professor wins!" : 
             "Draw!"}
          </div>
          {state.showReset && (
            <button className="reset-button" onClick={handleReset}>
              Play Again
            </button>
          )}
        </>
      )}
      <div className="grid-container">
        <div className="grid">
          {state.cells.map((cell, index) => (
            <div key={index} className={`cell ${cell}`} onClick={() => handleCellClick(index)}>
              {cell}
            </div>
          ))}
        </div>
        <div className="status-container">
          <div className="difficulty-level">
            Level {state.difficultyLevel}: {
              state.difficultyLevel === 1 ? "Apprentice" :
              state.difficultyLevel === 2 ? "Amateur" : "Master"
            }
          </div>
          <div className="score-board">
            <div className="score-item">
              <span className="player-name">Marie</span>
              <span className="score-value">{state.score.marie}</span>
            </div>
            <div className="score-item">
              <span className="player-name">Professeur Moulin</span>
              <span className="score-value">{state.score.moulin}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicTacToeMinigame;